/**
 * Questionnaire Service
 * API client for async questionnaire processing
 */

import axios from 'axios';
import config from '../config';
import {
  QuestionnaireWithItems,
  QuestionnaireListResponse,
  CreateQuestionnaireResponse,
  UpdateItemRequest,
  ApproveItemRequest,
  CustomField,
} from '../types';

const api = axios.create({
  baseURL: config.apiUrl,
  timeout: 60000, // 60 second timeout for uploads
});

/**
 * Upload a CSV and create a new questionnaire
 * @param uploadedBy - User ID (UUID), not email
 * @param uploadedByName - User's full name for display
 */
export const createQuestionnaire = async (
  file: File,
  projectId: string,
  clientName: string,
  customFields: CustomField[] = [],
  uploadedBy?: string,
  uploadedByName?: string
): Promise<CreateQuestionnaireResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('project_id', projectId);
  formData.append('client_name', clientName);
  formData.append('custom_fields', JSON.stringify(customFields));
  if (uploadedBy) {
    formData.append('uploaded_by', uploadedBy);
  }
  if (uploadedByName) {
    formData.append('uploaded_by_name', uploadedByName);
  }

  const response = await api.post('/api/questionnaires', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * List all questionnaires for a project
 */
export const listQuestionnaires = async (
  projectId: string,
  limit: number = 50
): Promise<QuestionnaireListResponse> => {
  const response = await api.get('/api/questionnaires', {
    params: { project_id: projectId, limit },
  });
  return response.data;
};

/**
 * Get a single questionnaire with all items
 */
export const getQuestionnaire = async (
  questionnaireId: string
): Promise<QuestionnaireWithItems> => {
  const response = await api.get(`/api/questionnaires/${questionnaireId}`);
  return response.data;
};

/**
 * Delete a questionnaire
 */
export const deleteQuestionnaire = async (
  questionnaireId: string
): Promise<void> => {
  await api.delete(`/api/questionnaires/${questionnaireId}`);
};

/**
 * Update a questionnaire item (user edit)
 */
export const updateQuestionnaireItem = async (
  itemId: string,
  data: UpdateItemRequest
): Promise<void> => {
  await api.patch(`/api/questionnaires/items/${itemId}`, data);
};

/**
 * Approve or reject a questionnaire item
 */
export const approveQuestionnaireItem = async (
  itemId: string,
  data: ApproveItemRequest
): Promise<{ message: string; is_approved: boolean }> => {
  const response = await api.patch(`/api/questionnaires/items/${itemId}/approve`, data);
  return response.data;
};

/**
 * Mark multiple items as saved to Q&A bank
 */
export const markItemsSavedToQA = async (
  itemIds: string[],
  savedBy?: string,
  savedByName?: string
): Promise<{ message: string; count: number }> => {
  const response = await api.patch('/api/questionnaires/items/mark-saved', {
    item_ids: itemIds,
    saved_by: savedBy,
    saved_by_name: savedByName,
  });
  return response.data;
};

/**
 * Reprocess a single item, optionally with additional context
 */
export const reprocessItem = async (
  questionnaireId: string,
  itemId: string,
  additionalContext?: string,
  editedBy?: string,
  editedByName?: string
): Promise<void> => {
  const body: Record<string, string> = {};
  if (additionalContext) {
    body.additional_context = additionalContext;
  }
  if (editedBy) {
    body.edited_by = editedBy;
  }
  if (editedByName) {
    body.edited_by_name = editedByName;
  }
  await api.post(`/api/questionnaires/${questionnaireId}/reprocess/${itemId}`, Object.keys(body).length > 0 ? body : undefined);
};

/**
 * Get export URL for a questionnaire
 */
export const getExportUrl = (questionnaireId: string): string => {
  return `${config.apiUrl}/api/questionnaires/${questionnaireId}/export`;
};

export interface URLMappingForExport {
  filename: string;
  public_url: string;
}

/**
 * Download questionnaire as CSV with URL mappings for proper source display
 */
export const exportQuestionnaire = async (
  questionnaireId: string,
  filename: string,
  urlMappings: URLMappingForExport[] = []
): Promise<void> => {
  // Use POST to send URL mappings so backend can display proper filenames
  const response = await api.post(
    `/api/questionnaires/${questionnaireId}/export`,
    { url_mappings: urlMappings },
    { responseType: 'blob' }
  );

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default {
  createQuestionnaire,
  listQuestionnaires,
  getQuestionnaire,
  deleteQuestionnaire,
  updateQuestionnaireItem,
  approveQuestionnaireItem,
  markItemsSavedToQA,
  reprocessItem,
  getExportUrl,
  exportQuestionnaire,
};

